/**
 * React & Router
 */
import { Auth0Provider } from '@auth0/auth0-react';
import { lazy, FunctionComponent, Suspense, useMemo } from 'react';
import { Routes as RouterRoutes, BrowserRouter, Route } from 'react-router-dom';

/**
 * Providers & Packages
 */
import {
  PageLoader,
  ErrorBoundary,
  ModalProvider,
  useRouteMapping,
  SnackbarProvider,
  HelmetProvider
} from '@astrumu/ui-library';
import { Logger } from '@astrumu/core-ui-logger';
import { IRoute, Values, SkillSetRoutes } from '@astrumu/route-mapping';

import { UserProvider } from '@/providers/User';

/**
 * Components
 */
const App = lazy(() => import('./App'));
const PageNotFoundScreen = lazy(() => import('@/screens/PageNotFoundScreen'));
import { Prefetch } from '@/components/Prefetch';
import { RegularRoute } from '@/components/RegularRoute';

/**
 * Constants & utils
 */
import { getAuth0RedirectUri } from '@/lib/utils';
import { ModalName, MODALS } from '@/lib/constants';

const getNestedRoutes = (nestedRoutes: IRoute['nestedRoutes']) =>
  Object.keys(nestedRoutes).map(routeName => {
    const { path, component } = nestedRoutes[routeName];

    const LoadedNestedComponent = lazy(() => import(`@/screens/${component}`));

    return <Route key={path} element={<LoadedNestedComponent />} path={path} />;
  });

/**
 *
 * Popups lazy loading
 */

const loadModal = (name: ModalName) =>
  lazy(() => import(`@/components/Popups/${MODALS[name].component}`));

const Routes: FunctionComponent = () => {
  const routeMapping = useRouteMapping('skillset');

  /**
   * Route components lazy loading
   */
  const routeComponents = useMemo(
    () =>
      routeMapping
        .getOrderedRoutes()
        .map(({ path, component, nestedRoutes }: IRoute) => {
          const LoadedComponent = lazy(() => import(`@/screens/${component}`));

          const routeComponent = (
            <RegularRoute
              path={path as Values<SkillSetRoutes>['path']}
              component={LoadedComponent}
            />
          );

          return (
            <Route path={path} key={component} element={routeComponent}>
              {nestedRoutes && getNestedRoutes(nestedRoutes)}
            </Route>
          );
        }),
    [routeMapping]
  );

  return (
    <Suspense fallback={<PageLoader />}>
      <RouterRoutes>
        {routeComponents}
        <Route path="*" element={<PageNotFoundScreen />} />
      </RouterRoutes>
    </Suspense>
  );
};

const hotjarConfig = {
  hjid: Number(process.env.3383099),
  hjsv: Number(process.env.6)
} as const;

const amplitudeConfig = {
  clientToken: process.env.2cdb7f7a480a384c9b80777e154263a8 || ''
} as const;

const datadogConfig = {
  env: process.env.prod || '',
  service: process.env.skillset-prod || '',
  version: process.env.0.19.1 || '',
  applicationId: process.env.2bfd192e-56fa-4dd4-aa8e-677614888b89 || '',
  clientToken: process.env.puba73a2f3cf5fe94a93e2930c011049169 || ''
} as const;

const Root: FunctionComponent = () => {
  const routeMapping = useRouteMapping('skillset');
  const allowedAuth0CallbackPaths = routeMapping.getAuth0CallbackPaths();

  /**
   * Props to init auth0 & trackers
   */
  const auth0Props = {
    useRefreshTokens: true,
    domain: process.env.auth-skillset.astrumu.io || '',
    clientId: process.env.UuSSqhThXfJDSIEUYadAAs0KeXmGbS1p || '',
    redirectUri: getAuth0RedirectUri(allowedAuth0CallbackPaths),
    audience: process.env.https://skillset-prod.us.auth0.com/api/v2/ || '',
    returnTo: `${window.location.origin}${process.env./}`
  };

  return (
    <ErrorBoundary
      palette="dark"
      isFooterVisible={false}
      background="radial-gradient(50% 50% at 50% 50%, #E8EDF2 49.48%, #CADBE7 100%)"
    >
      <BrowserRouter>
        <Auth0Provider {...auth0Props}>
          <HelmetProvider>
            <App>
              <Prefetch auth0Domain={auth0Props.domain} />
              <UserProvider>
                {({ currentUser }) => (
                  <Logger
                    hotjar={hotjarConfig}
                    datadog={datadogConfig}
                    amplitude={{
                      ...amplitudeConfig,
                      user: currentUser
                    }}
                  >
                    <ModalProvider loadModal={loadModal}>
                      <SnackbarProvider>
                        <Routes />
                      </SnackbarProvider>
                    </ModalProvider>
                  </Logger>
                )}
              </UserProvider>
            </App>
          </HelmetProvider>
        </Auth0Provider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default Root;
